import { FC, useState } from 'react'
import { createPortal } from 'react-dom'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { DatePicker } from 'antd'
import dayjs from 'dayjs'
import { shallowEqual, useSelector } from 'react-redux'
import { RootState } from '../../../../../setup'
import { UserModel } from '../../../../modules/auth/models/UserModel'
import { createExpertise } from '../../../../services/expertiseAdminService'
import { showErrorAlert } from '../../../../helpers/alert'
import { handleApiError } from '../../../../utils/handleApiError'

const TITLE_MIN = 3
const TITLE_MAX = 80
const DESCRIPTION_MIN = 20
const DESCRIPTION_MAX = 800
const RATE_MIN = 1

type Props = {
	show: boolean
	close: () => void
	partner: any
	onCreated?: () => void
}

export const CreateExpertiseModal: FC<Props> = ({ show, close, partner, onCreated }) => {
	const user = useSelector<RootState>(({ auth }) => auth.user, shallowEqual) as UserModel
	const [loading, setLoading] = useState<boolean>(false)

	const isAdmin = (user?.roles || []).some((r: string) => String(r).toUpperCase() === 'ADMIN')

	if (!show) return null

	return createPortal(
		<div className='nb-modal-overlay nb-modal-overlay--over-drawer' onClick={close}>
			<div className='nb-expertise-form' onClick={(e) => e.stopPropagation()}>
				{/* CLOSE BUTTON */}
				<button className='nb-expertise-form__close' onClick={close} type='button'>
					<svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
						<circle cx='12' cy='12' r='10' stroke='#808080' strokeWidth='1' />
						<path d='M9.17 9.17l5.66 5.66M14.83 9.17l-5.66 5.66' stroke='#808080' strokeWidth='1' />
					</svg>
				</button>

				{/* TITLE */}
				<div className='nb-expertise-form__title'>
					<h2>Create Expertise Record</h2>
					<span>EXPERT · {partner?.companyName}</span>
				</div>

				<Formik
					initialValues={{
						scheduleOn: '',
						hourlyRate: '',
						title: '',
						description: '',
					}}
					validationSchema={Yup.object({
						scheduleOn: Yup.string()
							.required('This field is required')
							.test('not-past', 'The date cannot be earlier than today', (value) =>
								!value || !dayjs(value).isBefore(dayjs().startOf('day'))
							),
						hourlyRate: Yup.number()
							.typeError('Enter a valid amount')
							.min(RATE_MIN, `The rate must be at least $${RATE_MIN.toFixed(2)}`)
							.required('This field is required'),
						title: Yup.string()
							.min(TITLE_MIN, `Minimum ${TITLE_MIN} characters`)
							.max(TITLE_MAX, `Maximum ${TITLE_MAX} characters`)
							.required('This field is required'),
						description: Yup.string()
							.min(DESCRIPTION_MIN, `Minimum ${DESCRIPTION_MIN} characters`)
							.max(DESCRIPTION_MAX, `Maximum ${DESCRIPTION_MAX} characters`)
							.required('This field is required'),
					})}
					onSubmit={async (values) => {
						// Sin el id del Partner la peticion se iria a .../experts/undefined/expertise/.
						// Se corta antes con un mensaje accionable en vez de un 'algo salio mal'.
						if (!partner?.id) {
							showErrorAlert({
								title: 'Error',
								message: 'Could not identify the expert account. Please sign out and sign in again.',
							})
							return
						}
						try {
							setLoading(true)
							await createExpertise(partner?.id, {
								title: values.title.trim(),
								description: values.description.trim(),
								hourlyRate: Number(values.hourlyRate),
								scheduleOn: dayjs(values.scheduleOn).toISOString(),
							})
							setLoading(false)
							showErrorAlert({
								title: 'Success',
								message: isAdmin
									? 'Expertise published successfully'
									: 'Expertise submitted for review',
							})
							onCreated?.()
							close()
						} catch (error: any) {
							setLoading(false)
							handleApiError(error)
						}
					}}
				>
					{({ values, setFieldValue }) => (
						<Form className='nb-expertise-form__body'>
							<div className='form-row'>
								{/* SCHEDULE ON */}
								<div className='form-field'>
									<label className='field-label'>Schedule On</label>
									<DatePicker
										className='underline-picker'
										format='DD/MM/YYYY'
										placeholder='DD/MM/YYYY'
										value={values.scheduleOn ? dayjs(values.scheduleOn) : null}
										disabledDate={(current) => current && current < dayjs().startOf('day')}
										popupStyle={{ zIndex: 10060 }}
										onChange={(value) =>
											setFieldValue('scheduleOn', value ? value.toISOString() : '')
										}
									/>
									<span className='field-hint'>
										Date on which the service will be offered / activated.
									</span>
									<div className='fv-plugins-message-container'>
										<div className='fv-help-block input-text-style fs-8'>
											<ErrorMessage name='scheduleOn' />
										</div>
									</div>
								</div>

								{/* RATE */}
								<div className='form-field'>
									<label className='field-label'>Rate</label>
									<div className='nb-expertise-form__rate'>
										<span className='nb-expertise-form__rate-prefix'>$</span>
										<Field
											name='hourlyRate'
											type='number'
											min={RATE_MIN}
											step='0.01'
											className='input'
										/>
										<span className='nb-expertise-form__rate-suffix'>/ hour</span>
									</div>
									<span className='field-hint'>USD · Hourly rate.</span>
									<div className='fv-plugins-message-container'>
										<div className='fv-help-block input-text-style fs-8'>
											<ErrorMessage name='hourlyRate' />
										</div>
									</div>
								</div>
							</div>

							{/* UNIT OF MEASURE — solo lectura. No forma parte del payload. */}
							<div className='form-field'>
								<label className='field-label'>Unit of Measure</label>
								<div className='nb-expertise-form__locked'>
									<span>Per hour</span>
									<span className='nb-expertise-form__locked-badge'>&#128274; Locked</span>
								</div>
								<span className='field-hint'>
									Only hourly billing is supported at this stage. Other units depend on the
									specific project scope.
								</span>
							</div>

							{/* EXPERTISE / TITLE */}
							<div className='form-field'>
								<label className='field-label'>Expertise / Title</label>
								<Field name='title' className='input' maxLength={TITLE_MAX} />
								<div className='nb-expertise-form__meta'>
									<span className='field-hint'>{TITLE_MIN}&ndash;{TITLE_MAX} characters</span>
									<span className='field-hint'>{values.title.length}/{TITLE_MAX}</span>
								</div>
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block input-text-style fs-8'>
										<ErrorMessage name='title' />
									</div>
								</div>
							</div>

							{/* MESSAGE / DESCRIPTION */}
							<div className='form-field'>
								<label className='field-label'>Message / Description</label>
								<Field
									as='textarea'
									name='description'
									className='input'
									rows={5}
									maxLength={DESCRIPTION_MAX}
								/>
								<div className='nb-expertise-form__meta'>
									<span className='field-hint'>
										{DESCRIPTION_MIN}&ndash;{DESCRIPTION_MAX} characters
									</span>
									<span className='field-hint'>
										{values.description.length}/{DESCRIPTION_MAX}
									</span>
								</div>
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block input-text-style fs-8'>
										<ErrorMessage name='description' />
									</div>
								</div>
							</div>

							{/* AVISO */}
							<div className='nb-expertise-form__notice'>
								<strong>Requires admin approval.</strong> As an Expert Partner, your service
								will remain in <em>Pending review</em> status until an administrator approves
								it. You&apos;ll receive a notification with the result.
							</div>

							{/* ACTIONS */}
							<div className='form-actions'>
								<button type='button' className='cancel-btn' onClick={close} disabled={loading}>
									Cancel
								</button>
								<button type='submit' className='save-btn' disabled={loading}>
									{loading ? 'Saving...' : isAdmin ? 'Publish' : 'Submit for review'}
								</button>
							</div>
						</Form>
					)}
				</Formik>
			</div>
		</div>,
		document.body
	)
}

export default CreateExpertiseModal
